import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { buildDownloadUrl } from '../../helpers/apiUtils';
import LoadingSpinner from '../LoadingSpinner';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface RecordHistoryModalProps {
  open: boolean;
  onClose: () => void;
  datum: string;
  eAffiliateId: string;
  anmeldungen: number;
  startDate: Date | null;
  endDate: Date | null;
}

interface HistoryRecord {
  snapshot_date: string;
  column_name: string;
  old_value: string;
  new_value: string;
  change_amount: number;
  change_percentage: number;
  is_decrease: number;
  created_at: string;
}

const getColumnDisplayName = (columnName: string): string => {
  const mapping: Record<string, string> = {
    abrechnungswert: 'Revenue',
    abrechnungswert_30: 'Revenue (30d)',
    anfo_bestaetigt_30: 'ANFOs (30d)',
    anfo_bestaetigt_ohne_v: 'ANFOs (ohne V)',
    costs: 'Kosten',
  };
  return mapping[columnName] || columnName;
};

export const RecordHistoryModal: React.FC<RecordHistoryModalProps> = ({
  open,
  onClose,
  datum,
  eAffiliateId,
  anmeldungen,
  startDate,
  endDate,
}) => {
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!open || !startDate || !endDate) {
      return;
    }

    setLoading(true);
    const fetchHistory = async () => {
      try {
        const options = {
          datum,
          e_affiliate_id: eAffiliateId,
          anmeldungen: anmeldungen.toString(),
          start_date: dateToSqlDate(startDate),
          end_date: dateToSqlDate(endDate),
        };

        const historyData = (await fetchData<Record<string, any>[]>(
          '/api/vnr/snapshots/record-history',
          options,
        )) as Record<string, any>[];

        setHistory(
          historyData.map((item) => ({
            snapshot_date: item.snapshot_date,
            column_name: item.column_name,
            old_value: item.old_value,
            new_value: item.new_value,
            change_amount: parseFloat(item.change_amount) || 0,
            change_percentage: parseFloat(item.change_percentage) || 0,
            is_decrease: item.is_decrease,
            created_at: item.created_at,
          })),
        );
        setLoading(false);
      } catch (err: any) {
        setError(err);
        setLoading(false);
      }
    };

    fetchHistory();
  }, [open, datum, eAffiliateId, anmeldungen, startDate, endDate]);

  const handleDownloadProof = async (snapshotDate: string) => {
    try {
      const url = buildDownloadUrl('/api/vnr/snapshots/download-proof', {
        snapshot_date: snapshotDate,
        datum,
        e_affiliate_id: eAffiliateId,
        anmeldungen: anmeldungen.toString(),
        format: 'csv',
      });

      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading proof:', err);
      alert('Fehler beim Herunterladen des Snapshot-Beweises');
    }
  };

  const handleDownloadAllProofs = () => {
    // Get unique snapshot dates
    const uniqueDates = Array.from(new Set(history.map((h) => h.snapshot_date)));

    uniqueDates.forEach((date) => {
      setTimeout(() => handleDownloadProof(date), 500);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="span">
          Änderungshistorie für Lead Record
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Datum:</strong> {datum}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: '100%',
              wordBreak: 'break-all',
            }}
          >
            <strong>Affiliate ID:</strong> {eAffiliateId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Anmeldungen:</strong> {anmeldungen}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && <LoadingSpinner />}
        {error && <Typography color="error">Error: {error.message}</Typography>}
        {!loading && !error && history.length === 0 && (
          <Typography>Keine Änderungen gefunden für diesen Record.</Typography>
        )}
        {!loading && !error && history.length > 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Button variant="outlined" onClick={handleDownloadAllProofs} size="small">
                Alle Snapshots herunterladen (
                {Array.from(new Set(history.map((h) => h.snapshot_date))).length})
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Snapshot Datum</TableCell>
                    <TableCell>Spalte</TableCell>
                    <TableCell align="right">Alter Wert</TableCell>
                    <TableCell align="right">Neuer Wert</TableCell>
                    <TableCell align="right">Änderung</TableCell>
                    <TableCell align="right">Änderung %</TableCell>
                    <TableCell align="center">Trend</TableCell>
                    <TableCell align="center">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((record, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: record.is_decrease
                          ? 'rgba(244, 67, 54, 0.1)'
                          : 'rgba(76, 175, 80, 0.1)',
                      }}
                    >
                      <TableCell>{record.snapshot_date}</TableCell>
                      <TableCell>
                        <Chip label={getColumnDisplayName(record.column_name)} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(parseFloat(record.old_value), 2)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(parseFloat(record.new_value), 2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: record.is_decrease ? '#d32f2f' : '#2e7d32',
                          fontWeight: 'bold',
                        }}
                      >
                        {formatNumber(record.change_amount, 2)}€
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: record.is_decrease ? '#d32f2f' : '#2e7d32',
                        }}
                      >
                        {formatNumber(record.change_percentage, 2)}%
                      </TableCell>
                      <TableCell align="center">
                        {record.is_decrease ? (
                          <TrendingDownIcon color="error" />
                        ) : (
                          <TrendingUpIcon color="success" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleDownloadProof(record.snapshot_date)}
                        >
                          Snapshot DL
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordHistoryModal;
